import React, { useState } from "react";
import { Button } from "./ui/button";
import { Download } from "lucide-react";
import { API, authToken } from "../lib/api";
import { toast } from "sonner";

/**
 * Triggers the corresponding /api/exports endpoint with the active filter
 * params and downloads the resulting .xlsx file.
 *
 * Props:
 *   endpoint   — "tasks" | "sales"
 *   params     — flat params dict (built from current queue state)
 *   prefix     — first segment of filename (e.g. "Insurance_Pending")
 *   scope      — second segment (location code or "ALL")
 *   monthLabel — third segment (e.g. "MAY_2026")
 */
export default function ExportButton({
  endpoint = "tasks", params = {}, prefix = "Export", scope = "ALL",
  monthLabel = null, disabled = false, label = "Download Excel",
  testid = "export-xlsx", iconOnly = false,
}) {
  const [busy, setBusy] = useState(false);

  const trigger = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams({
        ...params, prefix, scope, ...(monthLabel ? { month_label: monthLabel } : {}),
      });
      const url = `${API}/exports/${endpoint}?${qs.toString()}`;
      const uid = window.localStorage?.getItem("od360_active_user_id");
      const token = authToken();
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (uid) headers["X-User-Id"] = uid;
      const resp = await fetch(url, { headers });
      if (!resp.ok) {
        throw new Error(await resp.text() || `HTTP ${resp.status}`);
      }
      // Pull the server-suggested filename (matches `_xlsx_filename` on the backend)
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `${prefix}_${scope}.xlsx`;
      const blob = await resp.blob();
      const a = document.createElement("a");
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="default"
      size="sm"
      onClick={trigger}
      disabled={busy || disabled}
      className={iconOnly ? "h-9 w-9 flex-none px-0" : "gap-1.5"}
      data-testid={testid}
      aria-label={iconOnly ? label : undefined}
    >
      <Download className="h-3.5 w-3.5" />
      {!iconOnly && (busy ? "Exporting…" : label)}
    </Button>
  );
}
